import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'

import styles from '../projectDetail.module.css'

const SCROLL_DELTA = 180
const SCROLL_DURATION_MS = 280
/** Extra pixels past the last tab edge so the label isn’t clipped at the end. */
const END_SCROLL_PADDING = 16

const easeOutCubic = (t) => 1 - (1 - t) ** 3

const forceTabsRemeasure = () => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
}

/**
 * Left/right controls for scrolling Ant Design Tabs when labels overflow.
 * Lives in tabBarExtraContent (not the "…" operations slot) so rc-tabs
 * measures the visible strip correctly and the last tab is not clipped.
 */
const ProjectTabsScrollArrows = ({ rootSelector = '.viewTabs' }) => {
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [needsScroll, setNeedsScroll] = useState(false)
    const animationFrameRef = useRef(null)

    const getRoot = useCallback(() => {
        if (typeof document === 'undefined') return null
        return document.querySelector(rootSelector)
    }, [rootSelector])

    const getNavWrap = useCallback(() => {
        return getRoot()?.querySelector?.('.ant-tabs-nav-wrap') ?? null
    }, [getRoot])

    const getLastTabOverflowRight = useCallback(() => {
        const root = getRoot()
        const wrap = root?.querySelector?.('.ant-tabs-nav-wrap')
        const tabs = root?.querySelectorAll?.('.ant-tabs-nav-list .ant-tabs-tab')
        const lastTab = tabs?.length ? tabs[tabs.length - 1] : null
        if (!wrap || !lastTab) return 0

        const wrapRight = wrap.getBoundingClientRect().right
        const lastRight = lastTab.getBoundingClientRect().right
        return Math.max(0, lastRight - wrapRight + END_SCROLL_PADDING)
    }, [getRoot])

    const syncScrollState = useCallback(() => {
        const root = getRoot()
        const wrap = root?.querySelector?.('.ant-tabs-nav-wrap')
        const list = root?.querySelector?.('.ant-tabs-nav-list')
        if (!wrap || !list) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            setNeedsScroll(false)
            return
        }

        const wrapWidth = wrap.getBoundingClientRect().width
        const listWidth = list.scrollWidth || list.getBoundingClientRect().width
        const overflow = listWidth > wrapWidth + 1
        const lastOverflow = (() => {
            const tabs = list.querySelectorAll('.ant-tabs-tab')
            const lastTab = tabs.length ? tabs[tabs.length - 1] : null
            if (!lastTab) return 0
            return Math.max(
                0,
                lastTab.getBoundingClientRect().right -
                    wrap.getBoundingClientRect().right
            )
        })()

        const pingLeft = wrap.classList.contains('ant-tabs-nav-wrap-ping-left')
        const pingRight = wrap.classList.contains(
            'ant-tabs-nav-wrap-ping-right'
        )

        setNeedsScroll(overflow || lastOverflow > 1)
        setCanScrollLeft(pingLeft)
        // Keep right enabled while the last tab is still clipped, even if
        // rc-tabs thinks transform is already at its minimum.
        setCanScrollRight(pingRight || lastOverflow > 1)
    }, [getRoot])

    useEffect(() => {
        const wrap = getNavWrap()
        if (!wrap) return undefined

        syncScrollState()
        const remasureId = window.setTimeout(() => {
            forceTabsRemeasure()
            syncScrollState()
        }, 0)

        const observer = new MutationObserver(syncScrollState)
        observer.observe(wrap, {
            attributes: true,
            attributeFilter: ['class'],
        })

        window.addEventListener('resize', syncScrollState)
        return () => {
            window.clearTimeout(remasureId)
            observer.disconnect()
            window.removeEventListener('resize', syncScrollState)
            if (animationFrameRef.current != null) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [getNavWrap, syncScrollState])

    useEffect(() => {
        const root = getRoot()
        if (!root) return undefined

        const showArrows = needsScroll || canScrollLeft || canScrollRight
        const hadClass = root.classList.contains('tabs-has-scroll-arrows')
        root.classList.toggle('tabs-has-scroll-arrows', showArrows)

        // Remeasure after end-padding is applied so the last tab can fully scroll in.
        let remasureId
        if (showArrows !== hadClass) {
            remasureId = window.setTimeout(() => {
                forceTabsRemeasure()
                syncScrollState()
            }, 0)
        }

        return () => {
            if (remasureId != null) window.clearTimeout(remasureId)
        }
    }, [
        needsScroll,
        canScrollLeft,
        canScrollRight,
        getRoot,
        syncScrollState,
    ])

    useEffect(() => {
        return () => {
            getRoot()?.classList.remove('tabs-has-scroll-arrows')
        }
    }, [getRoot])

    const animateWheelScroll = useCallback(
        (wrap, totalDelta) => {
            if (!wrap || totalDelta === 0) {
                syncScrollState()
                return
            }

            if (animationFrameRef.current != null) {
                cancelAnimationFrame(animationFrameRef.current)
                animationFrameRef.current = null
            }

            const startTime = performance.now()
            let previousOffset = 0

            const step = (now) => {
                const progress = Math.min(
                    1,
                    (now - startTime) / SCROLL_DURATION_MS
                )
                const easedOffset = easeOutCubic(progress) * totalDelta
                const frameDelta = easedOffset - previousOffset
                previousOffset = easedOffset

                if (frameDelta !== 0) {
                    wrap.dispatchEvent(
                        new WheelEvent('wheel', {
                            deltaX: frameDelta,
                            deltaY: 0,
                            bubbles: true,
                            cancelable: true,
                        })
                    )
                }

                if (progress < 1) {
                    animationFrameRef.current = requestAnimationFrame(step)
                } else {
                    animationFrameRef.current = null
                    syncScrollState()
                }
            }

            animationFrameRef.current = requestAnimationFrame(step)
        },
        [syncScrollState]
    )

    /**
     * When rc-tabs has already hit its transform min but the last tab is still
     * clipped, nudge the nav-list transform directly for this edge case.
     */
    const forceRevealLastTab = useCallback(() => {
        const list = getRoot()?.querySelector?.('.ant-tabs-nav-list')
        if (!list) return

        const overflow = getLastTabOverflowRight()
        if (overflow <= 0) {
            syncScrollState()
            return
        }

        const current = new DOMMatrix(
            window.getComputedStyle(list).transform
        ).m41
        const next = current - overflow

        if (animationFrameRef.current != null) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
        }

        list.style.transition = `transform ${SCROLL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
        list.style.transform = `translate(${next}px, 0px)`

        window.setTimeout(() => {
            list.style.transition = ''
            syncScrollState()
        }, SCROLL_DURATION_MS + 20)
    }, [getLastTabOverflowRight, getRoot, syncScrollState])

    const scrollTabs = useCallback(
        (direction) => {
            const wrap = getNavWrap()
            if (!wrap) return

            if (direction === 'right') {
                const lastOverflow = getLastTabOverflowRight()
                const pingRight = wrap.classList.contains(
                    'ant-tabs-nav-wrap-ping-right'
                )

                if (!pingRight && lastOverflow > 0) {
                    forceRevealLastTab()
                    return
                }

                const totalDelta = Math.max(SCROLL_DELTA, lastOverflow)
                animateWheelScroll(wrap, totalDelta)
                return
            }

            animateWheelScroll(wrap, -SCROLL_DELTA)
        },
        [
            animateWheelScroll,
            forceRevealLastTab,
            getLastTabOverflowRight,
            getNavWrap,
        ]
    )

    const handleArrowActivate = (direction) => (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (direction === 'left' && !canScrollLeft) return
        if (direction === 'right' && !canScrollRight) return
        scrollTabs(direction)
    }

    const handleArrowKeyDown = (direction) => (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        handleArrowActivate(direction)(event)
    }

    if (!needsScroll && !canScrollLeft && !canScrollRight) {
        return null
    }

    return (
        <span
            className={styles.tabScrollArrows}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <span
                role="button"
                tabIndex={canScrollLeft ? 0 : -1}
                aria-label="Scroll tabs left"
                aria-disabled={!canScrollLeft}
                className={`${styles.tabScrollArrow} ${
                    !canScrollLeft ? styles.tabScrollArrowDisabled : ''
                }`}
                onClick={handleArrowActivate('left')}
                onKeyDown={handleArrowKeyDown('left')}
            >
                <Icon icon="mdi:chevron-left" width={18} height={18} />
            </span>
            <span
                role="button"
                tabIndex={canScrollRight ? 0 : -1}
                aria-label="Scroll tabs right"
                aria-disabled={!canScrollRight}
                className={`${styles.tabScrollArrow} ${
                    !canScrollRight ? styles.tabScrollArrowDisabled : ''
                }`}
                onClick={handleArrowActivate('right')}
                onKeyDown={handleArrowKeyDown('right')}
            >
                <Icon icon="mdi:chevron-right" width={18} height={18} />
            </span>
        </span>
    )
}

export default ProjectTabsScrollArrows
